import React, {
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { getAdminConstructionApiUrl } from '@adsk/acs-sc-web-platform-api';
import { useGetAllPaginatedData, CONTEXT_NAMES } from '@adsk/alloy-helpers';
import { getOrCreateGlobalContext } from '@adsk/alloy-react-context';

import { useCurrentProjectId } from './CurrentProjectIdContext';
import { removeOldDuplicatesBy } from './helpers/removeOldDuplicatesBy';
import { Pagination, User } from './types';

type DeletedUsersResponse = {
  pagination: Pagination;
  results: User[];
};

type DeletedUsersContextValue = {
  data: User[];
  loading: boolean;
  error: null | unknown;
  retry(): void;
  setEnabled?: (enabled: boolean) => void;
};

/**
 * Context for users list.
 */
const DeletedUsersContext = getOrCreateGlobalContext<DeletedUsersContextValue>(
  CONTEXT_NAMES.DeletedUsersContext,
);

const PAGE_LIMIT = 200;

type UseDeletedUsersOptionsType = {
  disable?: boolean;
};

const useDeletedUsersContext = () => {
  const contextValue = useContext(DeletedUsersContext);
  const hook = 'useDeletedUsers';
  const provider = 'DeletedUsersProvider';

  if (!contextValue) {
    console.error(`${hook} must be used within a ${provider}`);
  }

  return contextValue || {};
};

export const useDeletedUsers = (options?: UseDeletedUsersOptionsType) => {
  const { setEnabled, ...rest } = useDeletedUsersContext();
  const shouldFetchDeletedUsers = !options?.disable;
  useEffect(() => {
    if (setEnabled) {
      setEnabled(shouldFetchDeletedUsers);
    }
  }, [shouldFetchDeletedUsers, setEnabled]);

  return { ...rest, loading: options?.disable ? false : rest.loading };
};

export const DeletedUsersProvider: FC<PropsWithChildren> = ({ children }) => {
  const [enabled, setEnabled] = useState(false);
  const projectId = useCurrentProjectId();
  const adminApiUrl = getAdminConstructionApiUrl();

  const url =
    enabled && projectId
      ? `${adminApiUrl}/v1/projects/${projectId}/users?filter[status]=deleted&limit=${PAGE_LIMIT}`
      : '';

  const {
    loading: loadingData,
    error,
    data: dataFromBE,
    retry,
  } = useGetAllPaginatedData<DeletedUsersResponse>({
    url,
  });

  const data = useMemo(() => {
    return (
      dataFromBE &&
      removeOldDuplicatesBy(
        dataFromBE.flatMap((usersPages) => usersPages.results),
        (item) => item.id,
      )
    );
  }, [dataFromBE]);

  const loading = loadingData || !enabled;

  const value = useMemo(
    () => ({
      data,
      loading,
      error,
      retry,
      setEnabled: enabled ? undefined : setEnabled,
    }),
    [data, loading, error, retry, enabled],
  );

  return (
    <DeletedUsersContext.Provider value={value}>
      {children}
    </DeletedUsersContext.Provider>
  );
};

DeletedUsersProvider.displayName = 'DeletedUsersProvider';
